// pushNotifications.ts
// Registers the service worker, subscribes to web push, and syncs the
// subscription with the backend. Call subscribeToPush() after the user
// grants permission (i.e. from PushPermissionBanner).

const SW_PATH = '/sw.js'

async function getVapidPublicKey(): Promise<string> {
  const res = await fetch('/api/notifications/vapid-public-key')
  if (!res.ok) throw new Error('Failed to fetch VAPID public key')
  const data = await res.json()
  return data.publicKey as string
}

// Convert a URL-safe base64 string to a Uint8Array (required by the browser subscribe API)
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0))) as Uint8Array<ArrayBuffer>
}

export async function subscribeToPush(userId: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Push notifications are not supported in this browser.')
    return
  }

  // Register the service worker (no-op if already registered)
  const registration = await navigator.serviceWorker.register(SW_PATH)
  await navigator.serviceWorker.ready

  const publicKey = await getVapidPublicKey()

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  const { endpoint, keys } = subscription.toJSON() as {
    endpoint: string
    keys: { p256dh: string; auth: string }
  }

  await fetch(`/api/notifications/subscribe?userId=${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint, p256dh: keys.p256dh, auth: keys.auth }),
  })
}

export async function unsubscribeFromPush(userId: string): Promise<void> {
  if (!('serviceWorker' in navigator)) return

  const registration = await navigator.serviceWorker.getRegistration(SW_PATH)
  if (!registration) return

  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  const endpoint = subscription.endpoint
  await subscription.unsubscribe()

  await fetch(`/api/notifications/unsubscribe?userId=${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  })
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window
}

export function getPushPermissionState(): NotificationPermission {
  return 'Notification' in window ? Notification.permission : 'denied'
}
